/*
 * Lazzzy home hero — per-slide text entrance.
 *
 * The vendor <xo-carousel> owns the slideshow itself (the fade, looping, autoplay, the arrows).
 * This only animates the heading/subheading/button of whichever slide has just become active.
 *
 * How the active slide is detected, and why:
 *   The bundle does dispatch `xo:carousel:change`, but only as
 *   `Zo.change(this.providerElement, …)` behind an `if (this.providerElement)` guard — a plain
 *   <xo-carousel> has no provider element, so on this hero the event never fires. Verified in
 *   page: clicking the next arrow advanced the slide with zero change events observed.
 *   What *is* always true is the `xo-visible` attribute on the active slide — the vendor's own
 *   fade CSS keys off it — so a MutationObserver on that attribute is the dependable signal.
 *   The event listener is kept as a second trigger for setups that do have a provider; both
 *   route through playSlide(), which ignores a slide it has already played.
 *
 * Reduced motion is honoured through gsap.matchMedia(): under `prefers-reduced-motion: reduce`
 * the copy is simply placed, with no movement and no fade.
 */
(function () {
  'use strict';

  var SECTION = '.lazzzy-home-hero';
  var SLIDE = '.lazzzy-home-hero__slide';
  var CONTENT = '.lazzzy-home-hero__heading, .lazzzy-home-hero__subheading, .lazzzy-home-hero__btn';
  var PRE = 'lazzzy-home-hero--pre';

  function isVisible(slide) {
    var v = slide.getAttribute('xo-visible');
    return v !== null && v !== 'false';
  }

  function initHero(hero) {
    var carousel = hero.querySelector('xo-carousel');
    if (!carousel || hero.dataset.lazzzyMotion === 'on') return;
    hero.dataset.lazzzyMotion = 'on';

    if (!hero.querySelector(SLIDE)) return;

    var mm = window.gsap.matchMedia();

    mm.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        reduced: '(prefers-reduced-motion: reduce)'
      },
      function (context) {
        var reduced = context.conditions.reduced;
        var lastPlayed = null;

        function settle(slide) {
          window.gsap.set(slide.querySelectorAll(CONTENT), { autoAlpha: 1, y: 0 });
        }

        function playSlide(slide) {
          if (!slide || slide === lastPlayed) return;
          lastPlayed = slide;

          var targets = slide.querySelectorAll(CONTENT);
          if (!targets.length) return;

          if (reduced) {
            settle(slide);
            hero.classList.remove(PRE);
            return;
          }
          // set() applies synchronously; fromTo() would not apply its start values until the
          // next tick, and the pre-paint class is dropped in between — which showed up in page
          // as a single frame of fully-opaque copy before the tween began.
          window.gsap.set(targets, { autoAlpha: 0, y: 24 });
          hero.classList.remove(PRE);
          window.gsap.to(targets, {
            autoAlpha: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out',
            stagger: 0.12,
            overwrite: 'auto'
          });
        }

        function currentSlide() {
          var slides = hero.querySelectorAll(SLIDE);
          for (var i = 0; i < slides.length; i++) {
            if (isVisible(slides[i])) return slides[i];
          }
          return slides[0] || null;
        }

        var observer = new MutationObserver(function () {
          playSlide(currentSlide());
        });
        observer.observe(carousel, {
          subtree: true,
          attributes: true,
          attributeFilter: ['xo-visible']
        });

        function onChange() {
          playSlide(currentSlide());
        }
        carousel.addEventListener('xo:carousel:change', onChange);

        playSlide(currentSlide());

        // matchMedia reverts the tweens it created; the observer and listener are ours.
        return function () {
          observer.disconnect();
          carousel.removeEventListener('xo:carousel:change', onChange);
          Array.prototype.forEach.call(hero.querySelectorAll(SLIDE), settle);
          hero.classList.remove(PRE);
        };
      }
    );
  }

  function start() {
    if (!window.gsap) return;
    Array.prototype.forEach.call(document.querySelectorAll(SECTION), initHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The theme editor rebuilds a section's DOM in place on every settings change.
  document.addEventListener('shopify:section:load', function (event) {
    if (!window.gsap) return;
    Array.prototype.forEach.call(event.target.querySelectorAll(SECTION), initHero);
  });
})();
